import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * CurrencyRailContext — app-wide "active currency rail" filter.
 *
 * Value:
 *   activeRail: "ALL" | "USD" | "NGN"
 *   setActiveRail(v)
 *   filterRails(by_currency): filter a `by_currency` array from any API
 *   isActive(ccy): true if the given currency should render
 *
 * Persisted to localStorage so the choice survives across sessions.
 */
const CurrencyRailContext = createContext(null);
const STORAGE_KEY = "ledger.activeRail";

export function CurrencyRailProvider({ children }) {
  const [activeRail, setActiveRailState] = useState(() => {
    if (typeof window === "undefined") return "ALL";
    return localStorage.getItem(STORAGE_KEY) || "ALL";
  });

  const setActiveRail = useCallback((v) => {
    setActiveRailState(v);
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, v);
  }, []);

  const isActive = useCallback(
    (ccy) => activeRail === "ALL" || (ccy || "USD") === activeRail,
    [activeRail]
  );

  const filterRails = useCallback(
    (arr) => {
      if (!Array.isArray(arr)) return arr;
      if (activeRail === "ALL") return arr;
      return arr.filter((r) => (r.currency || "USD") === activeRail);
    },
    [activeRail]
  );

  useEffect(() => {
    // Keep other tabs in sync when the user flips the toggle in another window.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) setActiveRailState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <CurrencyRailContext.Provider value={{ activeRail, setActiveRail, isActive, filterRails }}>
      {children}
    </CurrencyRailContext.Provider>
  );
}

export function useCurrencyRail() {
  return useContext(CurrencyRailContext) || {
    activeRail: "ALL",
    setActiveRail: () => {},
    isActive: () => true,
    filterRails: (arr) => arr,
  };
}
